/**
 * Deterministic math. JS engines may round transcendental Math.* functions differently
 * (sin, exp, atan2, hypot, pow …), which would split a lockstep game. These versions use
 * only + − × ÷, sqrt and floor, which IEEE 754 makes bit-identical everywhere.
 * Accuracy is ~1e-12 relative — far below anything the game can perceive.
 */

export const PI = 3.141592653589793;
export const TAU = 6.283185307179586;
export const HALF_PI = 1.5707963267948966;
// Cody–Waite: 2π = TAU_HI + TAU_LO with TAU_HI on 24 mantissa bits, so k·TAU_HI is exact for |k| < 2^29
// and the reduction keeps full precision for large arguments too
const TAU_HI = 6.2831854820251465;
const TAU_LO = -1.748455600074497e-7;

/** sin on [-π/2, π/2]: odd Taylor series to x^19 (error < 1e-15 on the interval). */
function sinPoly(x: number): number {
  const x2 = x * x;
  return x * (1 + x2 * (-1 / 6 + x2 * (1 / 120 + x2 * (-1 / 5040 + x2 * (1 / 362880 + x2 * (-1 / 39916800
    + x2 * (1 / 6227020800 + x2 * (-1 / 1307674368000 + x2 * (1 / 355687428096000 + x2 * (-1 / 121645100408832000))))))))));
}

export function sin(x: number): number {
  if (!(x === x) || x === Infinity || x === -Infinity) return NaN;
  const k = Math.floor(x / TAU + 0.5);
  let r = (x - k * TAU_HI) - k * TAU_LO; // r ∈ [-π, π]
  if (r > HALF_PI) r = PI - r;
  else if (r < -HALF_PI) r = -PI - r;
  return sinPoly(r);
}

export function cos(x: number): number { return sin(x + HALF_PI); }

/** atan on [0, 1] via the reduction atan(x) = π/6 + atan((x√3 − 1)/(x + √3)) and an odd series. */
function atanUnit(x: number): number {
  const SQRT3 = 1.7320508075688772, TAN_PI_12 = 0.2679491924311227;
  let offset = 0;
  if (x > TAN_PI_12) { x = (x * SQRT3 - 1) / (x + SQRT3); offset = PI / 6; }
  // |x| ≤ tan(π/12) ≈ 0.268: the series converges fast; 12 terms give < 1e-16
  const x2 = x * x;
  let sum = 0;
  for (let n = 23; n >= 1; n -= 2) sum = 1 / n - x2 * sum;
  return offset + x * sum;
}

export function atan(x: number): number {
  if (!(x === x)) return NaN;
  const neg = x < 0; if (neg) x = -x;
  const r = x > 1 ? HALF_PI - atanUnit(1 / x) : atanUnit(x);
  return neg ? -r : r;
}

export function atan2(y: number, x: number): number {
  if (!(x === x) || !(y === y)) return NaN;
  if (x === 0) return y > 0 ? HALF_PI : y < 0 ? -HALF_PI : (1 / x < 0 ? (1 / y < 0 ? -PI : PI) : y);
  const a = atan(y / x);
  if (x > 0) return a;
  return y >= 0 ? a + PI : a - PI;
}

const LN2_HI = 0.6931471803691238, LN2_LO = 1.9082149292705877e-10, INV_LN2 = 1.4426950408889634;
const f64 = new Float64Array(1), u32 = new Uint32Array(f64.buffer);
// the high word of a little-endian double sits at index 1; detect once so big-endian hosts also work
const HI = new Uint8Array(new Uint16Array([1]).buffer)[0] === 1 ? 1 : 0;

/** 2^k for integer k in the normal range, built from the exponent bits. */
function pow2i(k: number): number {
  if (k > 1023) return Infinity;
  if (k < -1022) return k < -1074 ? 0 : pow2i(k + 52) / 4503599627370496;
  f64[0] = 0; u32[HI] = (k + 1023) << 20;
  return f64[0];
}

export function exp(x: number): number {
  if (!(x === x)) return NaN;
  if (x > 709.78) return Infinity;
  if (x < -745.13) return 0;
  const k = Math.floor(x * INV_LN2 + 0.5);
  const r = (x - k * LN2_HI) - k * LN2_LO; // |r| ≤ ln2/2
  // Taylor series to r^13 in Horner form: 1 + r(1 + r/2(1 + r/3(…))), error < 2e-16 on the interval
  let s = 1;
  for (let n = 13; n >= 1; n--) s = 1 + s * r / n;
  return s * pow2i(k);
}

/** x^n for an integer exponent by repeated squaring. */
export function powi(x: number, n: number): number {
  let result = 1, base = x, e = n < 0 ? -n : n;
  while (e > 0) { if (e & 1) result *= base; base *= base; e = Math.floor(e / 2); }
  return n < 0 ? 1 / result : result;
}

export const hypot = (x: number, y: number) => Math.sqrt(x * x + y * y);
export const clamp = (x: number, lo: number, hi: number) => (x < lo ? lo : x > hi ? hi : x);
export function smoothstep(x: number, a: number, b: number) {
  const t = clamp((x - a) / (b - a), 0, 1);
  return t * t * (3 - 2 * t);
}
